import { generate } from '@ant-design/colors';

export const primaryColor = '#576FF3';

export const darkMode = 'light';

type Fn = (...arg: any) => any;

type GenerateTheme = 'default' | 'dark';

export interface GenerateColorsParams {
  mixLighten: Fn;
  mixDarken: Fn;
  tinycolor: any;
  color?: string;
}

export function generateAntColors(color: string, theme: GenerateTheme = 'default') {
  return generate(color, {
    theme,
  });
}

export function getThemeColors(color?: string) {
  const tc = color || primaryColor;
  const lightColors = generateAntColors(tc);
  const primary = lightColors[5];
  const modeColors = generateAntColors(primary, 'dark');

  return [...lightColors, ...modeColors];
}

export function generateColors({
  color = primaryColor,
  mixLighten,
  mixDarken,
  tinycolor,
}: GenerateColorsParams) {
  const arr = new Array(19).fill(0);
  const lightens = arr.map((_t, i) => {
    return mixLighten(color, i / 5);
  });

  const darkens = arr.map((_t, i) => {
    return mixDarken(color, i / 5);
  });

  const alphaColors = arr.map((_t, i) => {
    return tinycolor(color)
      .setAlpha(i / 20)
      .toRgbString();
  });

  const shortAlphaColors = alphaColors.map((item) => item.replace(/\s/g, '').replace(/0\./g, '.'));

  const tinycolorLightens = arr
    .map((_t, i) => {
      return tinycolor(color)
        .lighten(i * 5)
        .toHexString();
    })
    .filter((item) => item !== '#ffffff');

  const tinycolorDarkens = arr
    .map((_t, i) => {
      return tinycolor(color)
        .darken(i * 5)
        .toHexString();
    })
    .filter((item) => item !== '#000000');
  return [
    ...lightens,
    ...darkens,
    ...alphaColors,
    ...shortAlphaColors,
    ...tinycolorDarkens,
    ...tinycolorLightens,
  ].filter((item) => !item.includes('-'));
}

export const colors = {
  // brand
  brand1: '#edf0ff',
  brand2: '#afc6f8',
  brand3: '#6283fa',
  brand4: '#576ff3',
  brand5: '#306ae8',
  // accent
  accent1: '#eafbff',
  accent2: '#aee7f9',
  accent3: '#80def2',
  accent4: '#63d3df',
  accent5: '#14a7c8',
  // success
  success1: '#ecfdf3',
  success2: '#a6f4c5',
  success3: '#6ce9a6',
  success4: '#32d583',
  success5: '#12b76a',
  // warning
  warning1: '#fffaeb',
  warning2: '#fedf89',
  warning3: '#fec84b',
  warning4: '#fdb022',
  warning5: '#f79009',
  // error
  error1: '#fef3f2',
  error2: '#fecdca',
  error3: '#ff7d7d',
  error4: '#f97066',
  error5: '#e06c64',
  // info
  info1: '#f0f9ff',
  info2: '#e0f2fe',
  info3: '#7cd4fd',
  info4: '#57ccef',
  info5: '#0ba5ec',
  // gray
  gray1: '#f4f4f4',
  gray2: '#f2f4f7',
  gray3: '#eaecf0',
  gray4: '#d0d5dd',
  gray5: '#98a2b3',
  // yellow
  yellow1: '#feffe6',
  yellow2: '#fffb8f',
  yellow3: '#fff566',
  yellow4: '#ffec3d',
  yellow5: '#fadb14',
  // volcano
  volcano1: '#fff2e8',
  volcano2: '#ffbb96',
  volcano3: '#ff9c6e',
  volcano4: '#ff7a45',
  volcano5: '#fa541c',
  // lime
  lime1: '#fcffe6',
  lime2: '#eaff8f',
  lime3: '#d3f261',
  lime4: '#bae637',
  lime5: '#a0d911',
  // green
  green1: '#f6ffed',
  green2: '#b7eb8f',
  green3: '#95de64',
  green4: '#73d13d',
  green5: '#52c41a',
  // teal
  teal1: '#f0ffff',
  teal2: '#afecef',
  teal3: '#7edce2',
  teal4: '#1ec3d0',
  teal5: '#0694a2',
  // purple
  purple1: '#f9f0ff',
  purple2: '#d3adf7',
  purple3: '#b37feb',
  purple4: '#9254de',
  purple5: '#722ed1',
  // magenta
  magenta1: '#fff0f6',
  magenta2: '#ffadd2',
  magenta3: '#ff85c0',
  magenta4: '#f759ab',
  magenta5: '#eb2f96',
  // font
  font1: '#333333',
  font2: '#666666',
  font3: '#aaaaaa',
  disabled: '#c9cdd4',
  icon: '#5a5c60',
  // else
  label: '#61f0e7',
  card: '#f1f4f9',
  content: '#f2f5fa',
};
